import { useTheme } from '@mui/material';

/**
 * Available color options with their metadata
 * This is the single source of truth for appointment type colors
 */
export interface ColorOption {
  value: string;
  hex: string;
  label: string;
}

const STATIC_COLOR_OPTIONS: ColorOption[] = [
  { value: 'color2', hex: '#E57373', label: 'Coral' },
  { value: 'color3', hex: '#81C784', label: 'Green' },
  { value: 'color4', hex: '#FFB74D', label: 'Amber' },
  { value: 'color5', hex: '#BA68C8', label: 'Lavender' },
  { value: 'color6', hex: '#4DB6AC', label: 'Teal' },
  { value: 'color7', hex: '#FF8A65', label: 'Peach' },
  { value: 'color8', hex: '#A1887F', label: 'Taupe' },
  { value: 'color9', hex: '#90A4AE', label: 'Slate' },
  { value: 'color10', hex: '#F06292', label: 'Pink' },
];

/**
 * Hook to get all available appointment type colors
 * Returns color options with actual hex values from theme
 *
 * @returns Array of color options with value, hex, and label
 *
 * @example
 * const colors = useAvailableColors();
 * // [{ value: 'color1', hex: '#6C00CC', label: 'Purple' }, ...]
 */
export const useAvailableColors = (): ColorOption[] => {
  const theme = useTheme();

  return [
    { value: 'color1', hex: theme.palette.primary.main, label: 'Purple' },
    ...STATIC_COLOR_OPTIONS,
  ];
};

/**
 * Get human-readable label for a color enum
 *
 * @param colorEnum - Backend color enum (e.g., "color1", "color2")
 * @returns Human-readable label for the color
 *
 * @example
 * getColorLabel('color1'); // Returns "Purple"
 * getColorLabel('color2'); // Returns "Coral"
 * getColorLabel('unknown'); // Returns "Unknown"
 */
export const getColorLabel = (colorEnum: string): string => {
  const labels: Record<string, string> = {
    color1: 'Purple',
    ...Object.fromEntries(STATIC_COLOR_OPTIONS.map(option => [option.value, option.label])),
  };

  return labels[colorEnum] ?? 'Unknown';
};

/**
 * Pure (non-hook) enum→swatch resolver for adapters and other non-component
 * code. Returns a value MUI's `sx` color system understands: the theme token
 * 'primary.main' for color1 (so it stays theme-reactive), a hex for the
 * static palette, and null when the enum is missing or unknown — callers
 * render their own placeholder.
 *
 * Component code should prefer the useColorFromEnum hook, which resolves
 * against the live theme and falls back to primary.
 *
 * @example
 * getColorSwatchFromEnum('color1'); // 'primary.main'
 * getColorSwatchFromEnum('color2'); // '#E57373'
 * getColorSwatchFromEnum(null);     // null
 */
export const getColorSwatchFromEnum = (
  colorEnum: string | null | undefined
): string | null => {
  if (!colorEnum) return null;
  if (colorEnum === 'color1') return 'primary.main';
  return (
    STATIC_COLOR_OPTIONS.find(option => option.value === colorEnum)?.hex ?? null
  );
};

/**
 * Hook to map backend color enum to theme palette color
 *
 * Backend provides color identifiers ("color1", "color2", etc.) for
 * appointments and encounters. This hook maps those identifiers
 * to actual theme palette colors or specific hex values.
 *
 * Current mappings:
 * - "color1" → theme.palette.primary.main (purple)
 * - "color2" → #E57373 (coral)
 *
 * @param colorEnum - Backend color enum (e.g., from appointment.color or encounter.color)
 * @returns Hex color string from theme palette or predefined color
 *
 * @example
 * const color = useColorFromEnum("color1"); // Returns "#6C00CC"
 * const color = useColorFromEnum("color2"); // Returns "#E57373"
 * const color = useColorFromEnum(null); // Returns primary.main (fallback)
 */
export const useColorFromEnum = (colorEnum?: string | null): string => {
  const theme = useTheme();

  // Default fallback to primary color
  const fallback = theme.palette.primary.main;

  if (!colorEnum) {
    return fallback;
  }

  // Map backend color enum to theme palette or specific colors
  switch (colorEnum) {
    case 'color1':
      return theme.palette.primary.main;
    case 'color2':
    case 'color3':
    case 'color4':
    case 'color5':
    case 'color6':
    case 'color7':
    case 'color8':
    case 'color9':
    case 'color10':
      return STATIC_COLOR_OPTIONS.find(option => option.value === colorEnum)?.hex ?? fallback;
    default:
      console.warn(`Unknown color enum: "${colorEnum}", using fallback`);
      return fallback;
  }
};
